import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Select, Button, Tag, Spin, Empty, Tooltip, message } from 'antd';
import {
  SearchOutlined, CloseCircleOutlined, PlusOutlined, EyeOutlined, QrcodeOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { educationAssetService, educationSessionService } from '@iso/services';
import SessionQrModal from './SessionQrModal';
import SessionDetailModal from './SessionDetailModal';
import {
  SESSION_DRAFT, SESSION_ACTIVE, SESSION_COMPLETED, sessionStatusMeta,
} from './sessionStatus';

const { Option } = Select;

/**
 * Every training session on this property, in one list.
 *
 * Sessions already existed but were only reachable by opening the Assets tab,
 * drilling into the right asset and scrolling its history — so running one and
 * then getting back to its QR code or its attendee list read as impossible.
 * The Records tab is no substitute: it lists one row per PERSON per training,
 * so a session nobody has registered for yet does not appear there at all,
 * which is exactly the session a trainer needs to open.
 */
const TrainingSessions = ({ propertyId, onRunSession, canRunSession, refreshKey }) => {
  const [sessions, setSessions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAssetId, setFilterAssetId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activating, setActivating] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  // Both hold an id rather than a boolean, so each modal loads the session
  // itself and stays ignorant of this table.
  const [qrSessionId, setQrSessionId] = useState(null);
  const [detailSessionId, setDetailSessionId] = useState(null);

  const fetchSessions = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (search) params.search = search;
      if (filterAssetId) params.education_asset_id = filterAssetId;
      if (filterStatus) params.status = filterStatus;
      const res = await educationSessionService.getByProperty(propertyId, params);
      if (res) {
        setSessions(res.education_sessions || []);
        setTotalItems(res.total_item || 0);
        // The page size is the server's (Yii::$app->params['pageSize']), not
        // ours, and it is not in the response — so read it off page 1, which is
        // the only page guaranteed to be full when more pages exist. Measuring
        // any other page would catch a partial last page and shrink the pager
        // to that count, inventing pages that do not exist.
        const returned = (res.education_sessions || []).length;
        if (currentPage === 1 && returned > 0 && res.total_item > returned) {
          setPageSize(returned);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [propertyId, currentPage, search, filterAssetId, filterStatus]);

  // Activating from the list, without opening the session first: on the morning
  // of a session that is the only thing the trainer wants to do, and the QR
  // opens straight afterwards because that is invariably the next click.
  const activateSession = async (session) => {
    setActivating(session.id);
    try {
      const res = await educationSessionService.setStatus(session.id, SESSION_ACTIVE);
      if (res && res.code === '0000') {
        message.success(res.message || 'Session activated.');
        setQrSessionId(session.id);
        fetchSessions();
      } else {
        message.error((res && res.message) || 'The session could not be activated.');
      }
    } catch (e) {
      message.error('The session could not be activated.');
    } finally {
      setActivating(null);
    }
  };

  useEffect(() => {
    // Debounced so typing in the search box doesn't fire a request per
    // keystroke; the filter and page changes ride the same timer harmlessly.
    const t = setTimeout(fetchSessions, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchSessions, refreshKey]);

  useEffect(() => {
    if (!propertyId) return;
    educationAssetService.getAll(propertyId).then((res) => {
      if (res) setAssets(res.education_assets || []);
    }).catch(() => { /* the filter is optional; an empty list just hides it */ });
  }, [propertyId]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'session_date',
      key: 'session_date',
      render: d => (d ? moment(d).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Training',
      key: 'training',
      render: (_, r) => (
        <span style={{ fontWeight: 600 }}>
          {r.training_name || (r.education_asset && r.education_asset.name) || '—'}
        </span>
      ),
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer_name',
      key: 'trainer_name',
      render: t => t || '—',
    },
    {
      title: 'Location',
      dataIndex: 'location_name',
      key: 'location_name',
      render: l => l || '—',
    },
    {
      title: 'Time',
      key: 'time',
      render: (_, r) => (r.start_time ? `${r.start_time}${r.finish_time ? ` – ${r.finish_time}` : ''}` : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const meta = sessionStatusMeta(s);
        return (
          <Tooltip title={meta.hint}>
            <Tag color={meta.color}>{meta.label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Attendees',
      key: 'attendees',
      align: 'center',
      render: (_, r) => {
        const n = (r.trainees || []).length;
        return n === 0
          ? <span style={{ color: '#bfbfbf' }}>None yet</span>
          : <Tag color="blue">{n}</Tag>;
      },
    },
    {
      title: 'Assessment',
      key: 'assessment',
      render: (_, r) => {
        if (!r.assessment || !r.assessment.has_assessment) {
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        }
        return (
          <Tooltip title={r.assessment.form_name}>
            <Tag color={r.assessment.scored ? 'green' : 'default'}>
              {r.assessment.scored ? `Marked · ${r.assessment.pass_mark}%` : 'Not marked'}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Tooltip title="Open the session — attendees, assessment and competency">
            <Button type="text" icon={<EyeOutlined />} onClick={() => setDetailSessionId(r.id)} />
          </Tooltip>
          {r.status === SESSION_DRAFT && canRunSession ? (
            <Tooltip title="Activate — opens the session and shows the QR code">
              <Button
                type="text"
                icon={<PlayCircleOutlined />}
                loading={activating === r.id}
                onClick={() => activateSession(r)}
              />
            </Tooltip>
          ) : (
            <Tooltip
              title={r.status === SESSION_DRAFT
                ? 'This session is still a draft — activate it to open registration'
                : 'Show the QR code for attendees to scan'}
            >
              {/* A draft's QR registers nobody, so the button is disabled
                  rather than handing out a code that silently does nothing. */}
              <Button
                type="text"
                icon={<QrcodeOutlined />}
                disabled={r.status === SESSION_DRAFT}
                onClick={() => setQrSessionId(r.id)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div className="filter-row">
          <Input
            className="search-bar training-search-bar"
            prefix={<SearchOutlined />}
            suffix={search ? <CloseCircleOutlined onClick={() => { setSearch(''); setCurrentPage(1); }} /> : null}
            placeholder="Search by training or location..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <Select
            className="filter-select"
            value={filterStatus}
            onChange={v => { setFilterStatus(v); setCurrentPage(1); }}
            placeholder="All Statuses"
          >
            <Option value="">All Statuses</Option>
            <Option value={SESSION_DRAFT}>Draft</Option>
            <Option value={SESSION_ACTIVE}>Running</Option>
            <Option value={SESSION_COMPLETED}>Completed</Option>
          </Select>
          <Select
            className="filter-select"
            value={filterAssetId}
            onChange={v => { setFilterAssetId(v); setCurrentPage(1); }}
            placeholder="All Training Assets"
            showSearch
            optionFilterProp="children"
          >
            <Option value="">All Training Assets</Option>
            {assets.map(a => <Option key={a.id} value={a.id}>{a.name}</Option>)}
          </Select>
        </div>
        {canRunSession && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: '#0B2A1B', borderColor: '#0B2A1B', borderRadius: 8 }}
            onClick={onRunSession}
          >
            Run New Training Session
          </Button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : sessions.length === 0 ? (
        <Empty description="No training sessions yet" style={{ marginTop: 60 }} />
      ) : (
        <Table
          dataSource={sessions}
          columns={columns}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: totalItems,
            onChange: setCurrentPage,
            showSizeChanger: false,
            showTotal: (total) => `${total} sessions`,
          }}
          scroll={{ x: 900 }}
        />
      )}

      <SessionDetailModal
        visible={detailSessionId !== null}
        sessionId={detailSessionId}
        propertyId={propertyId}
        onClose={() => setDetailSessionId(null)}
        // Competency and assessment changes alter the attendee counts and
        // assessment column on this table, so re-read once the modal reports one.
        onChanged={fetchSessions}
      />

      <SessionQrModal
        visible={qrSessionId !== null}
        sessionId={qrSessionId}
        onClose={() => setQrSessionId(null)}
      />
    </div>
  );
};

export default TrainingSessions;
